import { useEffect, useRef, useState } from "react";
import flags from "react-phone-number-input/flags";
import {
  getCountries,
  getCountryCallingCode,
} from "react-phone-number-input";
import en from "react-phone-number-input/locale/en.json";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";

const flagBox = {
  width: "20px",
  height: "14px",
  flex: "0 0 20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  borderRadius: 3,
  boxShadow: "0 0 0 1px rgba(15,27,42,0.08)",
  fontSize: 12,
  lineHeight: 1,
};

export default function CountrySelectForModal({
  value,
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // The menu is position: fixed (as before); these coordinates pin it under
  // the button so it can't drift or be clipped inside a dialog.
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 260 });

  const wrapperRef = useRef(null);

  const countries = getCountries();

  const filteredCountries = countries.filter((c) =>
    en[c]
      ?.toLowerCase()
      .includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = wrapperRef.current?.getBoundingClientRect();
      if (!r) return;
      const width = Math.max(260, r.width);
      const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
      const below = window.innerHeight - r.bottom;
      const top = below < 330 && r.top > 330 ? r.top - 6 - 330 : r.bottom + 6;
      setMenuPos({ top, left, width });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  const SelectedFlag = flags[value];

  return (
    <div
      ref={wrapperRef}
      style={{
        position: "relative",
        width: "100%",
        fontFamily: FONT,
      }}
    >
      {/* SELECT BUTTON */}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          width: "100%",
          height: 42,
          boxSizing: "border-box",
          border: `1px solid ${open ? PRIMARY : FIELD_EDGE}`,
          borderRadius: "10px",
          background: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 12px",
          cursor: "pointer",
          fontFamily: MONO,
          fontSize: "13.5px",
          fontWeight: 600,
          color: INK,
          position: 'relative'
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <div style={flagBox}>
            {SelectedFlag ? (
              <SelectedFlag />
            ) : (
              "🌐"
            )}
          </div>

          <span>
            +{getCountryCallingCode(value)}
          </span>
        </div>

        <svg
          width="10"
          height="10"
          viewBox="0 0 10 6"
          style={{
            color: open ? PRIMARY : MUTED,
            transform: open
              ? "rotate(180deg)"
              : "rotate(0deg)",
            transition: "0.2s",
          }}
        >
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      </button>

      {/* DROPDOWN */}
      {open && (
        <div
          style={{
            position: "fixed",
            top: menuPos.top,
            left: menuPos.left,
            width: menuPos.width,
            background: "#fff",
            border: `1px solid ${LINE}`,
            borderRadius: "12px",
            boxShadow:
              "0 16px 36px -12px rgba(15,27,42,0.28)",
            zIndex: 999999,
            overflow: "hidden",  
          }}
        >
          {/* SEARCH */}
          <div
            style={{
              padding: "8px",
              borderBottom:
                "1px solid #EDF1F5",
            }}
          >
            <input
              type="text"
              placeholder="Search country..."
              value={search}
              onChange={(e) =>
                setSearch(e.target.value)
              }
              autoFocus
              style={{
                width: "100%",
                boxSizing: "border-box",
                height: 36,
                padding: "0 10px",
                background: "#F7F9FC",
                border: `1px solid ${FIELD_EDGE}`,
                borderRadius: "9px",
                outline: "none",
                fontFamily: FONT,
                fontSize: "13.5px",
                color: INK,
              }}
            />
          </div>

          {/* LIST */}
          <div
            style={{
              maxHeight: "260px",
              overflowY: "auto",
              padding: 4,
            }}
          >
            {filteredCountries.map((country) => {
              const FlagComponent =
                flags[country];
              const on = country === value;

              return (
                <button
                  key={country}
                  type="button"
                  onClick={() => {
                    onChange(country);
                    setOpen(false);
                    setSearch("");
                  }}
                  style={{
                    width: "100%",
                    border: "none",
                    borderRadius: 8,
                    background: on ? "#EAF2FB" : "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      "space-between",
                    gap: 10,
                    minHeight: 36,
                    padding: "6px 10px",
                    cursor: "pointer",
                    fontFamily: FONT,
                    fontSize: "13.5px",
                    fontWeight: on ? 700 : 500,
                    color: on ? "#1B4F82" : INK,
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) => {
                    if (!on) e.currentTarget.style.background =
                      "#F4F7FA";
                  }}
                  onMouseLeave={(e) => {
                    if (!on) e.currentTarget.style.background =
                      "#fff";
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      minWidth: 0,
                    }}
                  >
                    <div style={flagBox}>
                      {FlagComponent && (
                        <FlagComponent />
                      )}
                    </div>

                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{en[country]}</span>
                  </div>

                  <span
                    style={{
                      flex: "0 0 auto",
                      fontFamily: MONO,
                      fontSize: 12,
                      color: MUTED,
                    }}
                  >
                    +
                    {getCountryCallingCode(
                      country
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
